// Entry: builds the real services (Supabase auth, api client, LiveKit room,
// browser media) and mounts the app. LiveKit loads only when a room opens.
import '@livekit/components-styles';
import './styles.css';
import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createApiClient } from './api/client.js';
import { App } from './App.js';
import { createSupabaseAuth } from './auth/supabase.js';
import { readConfig } from './config.js';
import { COPY } from './copy.js';
import { createAuthFailureBus, ServicesProvider } from './services.js';
import type { LiveRoomProps, Services } from './services.js';

const LazyLiveKitRoom = lazy(() => import('./pages/LiveKitRoomView.js'));

function LiveRoom(props: LiveRoomProps) {
  return (
    <Suspense fallback={<p className="page muted">{COPY.loading}</p>}>
      <LazyLiveKitRoom {...props} />
    </Suspense>
  );
}

const config = readConfig(import.meta.env);
const auth = createSupabaseAuth({
  url: config.supabaseUrl,
  anonKey: config.supabaseAnonKey,
});
const authFailures = createAuthFailureBus();
const services: Services = {
  auth,
  api: createApiClient({
    baseUrl: config.apiBaseUrl,
    getAccessToken: () => auth.getAccessToken(),
    onAuthFailure: (err) => authFailures.emit(err),
  }),
  authFailures,
  LiveRoom,
  media: navigator.mediaDevices ?? null,
  confirm: (message) => window.confirm(message),
};

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');
createRoot(root).render(
  <StrictMode>
    <ServicesProvider services={services}>
      <App />
    </ServicesProvider>
  </StrictMode>,
);
